import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Divider from '@mui/material/Divider';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LogoutIcon from '@mui/icons-material/Logout';
import { alpha } from '@mui/material/styles';
import COLORS from '../../../constants/colors';
import { APP_HEADER_HEIGHT } from '../../../constants/layout';
import { authSelector } from '../../../redux/features/auth/auth.selectors';
import { AppBar } from './NavbarElements';

interface INavbarProps {
  open: boolean;
  handleLogout: () => void;
}

const HAIRLINE = alpha(COLORS.MAIN_DARK, 0.08);

/** First letters of the first two words, e.g. "Kamal Abd El-Kader" -> "KA". */
function getInitials(name?: string) {
  return (name || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join('')
    .toUpperCase();
}

function Navbar({ open, handleLogout }: INavbarProps) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const { user } = useSelector(authSelector);
  const { i18n } = useTranslation();
  // const isArabic = i18n.language === 'ar';

  const isMenuOpen = Boolean(anchorEl);
  const menuId = 'primary-account-menu';

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleChangeLanguage = () => {
    i18n.changeLanguage(i18n.language === 'en' ? 'ar' : 'en');
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      id={menuId}
      keepMounted
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      open={isMenuOpen}
      onClose={handleMenuClose}
      PaperProps={{
        sx: {
          mt: '8px',
          minWidth: '248px',
          borderRadius: '12px',
          border: `1px solid ${HAIRLINE}`,
          boxShadow: `0px 8px 24px ${alpha(COLORS.MAIN_DARK, 0.1)}`,
        },
      }}
      MenuListProps={{ sx: { py: 0 } }}
    >
      {/* Identity recap: the trigger is compact, so the full name and email live here. */}
      <Box sx={{ px: '16px', py: '12px' }}>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: COLORS.MAIN_DARK, lineHeight: '20px' }} noWrap>
          {user?.name}
        </Typography>
        {user?.email && (
          <Typography sx={{ fontSize: '0.75rem', color: COLORS.LIGHT_GRAY, lineHeight: '18px' }} noWrap>
            {user.email}
          </Typography>
        )}
      </Box>
      <Divider sx={{ borderColor: HAIRLINE }} />
      <Box sx={{ py: '6px' }}>
        {/* <MenuItem onClick={handleMenuClose}>Profile</MenuItem> */}
        <MenuItem onClick={handleLogout} sx={{ mx: '6px', borderRadius: '8px', fontSize: '0.875rem' }}>
          <ListItemIcon sx={{ minWidth: '0!important', mr: '10px' }}>
            <LogoutIcon sx={{ fontSize: '18px', color: COLORS.LIGHT_GRAY }} />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Box>
    </Menu>
  );

  return (
    <>
      <AppBar position="fixed" open={open} elevation={0}>
        <Toolbar
          sx={{
            minHeight: `${APP_HEADER_HEIGHT}px!important`,
            height: '100%',
            px: '24px!important',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '12px',
          }}
        >
          <ButtonBase
            aria-label="Account menu"
            aria-controls={isMenuOpen ? menuId : undefined}
            aria-haspopup="true"
            aria-expanded={isMenuOpen}
            onClick={handleProfileMenuOpen}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              py: '5px',
              pl: '5px',
              pr: { xs: '5px', sm: '10px' },
              borderRadius: '10px',
              border: `1px solid ${isMenuOpen ? alpha(COLORS.MAIN_DARK, 0.16) : HAIRLINE}`,
              backgroundColor: isMenuOpen ? alpha(COLORS.MAIN_DARK, 0.04) : COLORS.TRANSPARENT,
              transition: 'background-color .15s ease, border-color .15s ease',
              '&:hover': {
                backgroundColor: alpha(COLORS.MAIN_DARK, 0.04),
                borderColor: alpha(COLORS.MAIN_DARK, 0.16),
              },
              '&.Mui-focusVisible': {
                outline: `2px solid ${COLORS.PRIMARY_BLUE}`,
                outlineOffset: '2px',
              },
            }}
          >
            <Avatar
              sx={{
                width: 34,
                height: 34,
                fontSize: '0.8125rem',
                fontWeight: 600,
                letterSpacing: '0.02em',
                color: COLORS.PRIMARY_BLUE,
                backgroundColor: alpha(COLORS.PRIMARY_BLUE, 0.12),
              }}
            >
              {getInitials(user?.name)}
            </Avatar>
            <Box sx={{ display: { xs: 'none', sm: 'block' }, textAlign: 'left', minWidth: 0, maxWidth: '190px' }}>
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  lineHeight: '18px',
                  color: COLORS.MAIN_DARK,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {user?.name}
              </Typography>
              {user?.role?.name && (
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    lineHeight: '16px',
                    color: COLORS.LIGHT_GRAY,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {user.role.name}
                </Typography>
              )}
            </Box>
            <KeyboardArrowDownIcon
              sx={{
                display: { xs: 'none', sm: 'block' },
                fontSize: '18px',
                color: COLORS.LIGHT_GRAY,
                transform: isMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform .2s ease',
              }}
            />
          </ButtonBase>
        </Toolbar>
      </AppBar>
      {renderMenu}
    </>
  );
}

export default Navbar;
